import { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { ContainerFull } from '../../components/ContainerFull';
import { Heading } from '../../components/Heading';
import { Wrapper } from '../../components/Wrapper';
import { CardCourse } from './components/CardCourse';

import { toast } from 'react-hot-toast';
import { cleanActionSelectedCourse, getAllCoursesByFilters } from '../../redux/actions/course';
import { cleanActionNoticeMessages } from '../../redux/actions/noticeMessage';
import {
    AdjustmentsHorizontalIcon,
    SquaresPlusIcon,
    DocumentArrowDownIcon,
    ArrowsUpDownIcon,
} from '@heroicons/react/20/solid';
import { Tooltip, Zoom } from '@mui/material';
import { DrawerFiltersCourse } from './DrawerFiltersCourse';
import { DrawerCourseForm } from './DrawerCourseForm';
import { SkeletonCardCourse } from './skeleton/SkeletonCardCourse';
import { PopoverRadioFilter } from '../../components/popoverRadioFilter/PopoverRadioFilter';
import { optionsSort } from '../../static/data';
import { SkeletonTableCourse } from './skeleton/SkeletonTableCourse';
import { CourseTableView } from './CourseTableView';
import { verifyStateApp } from '../../redux/actions/stateApp';
import { exportToExcel } from '../../common/importDataToExcel';
import { CardCourseDisplay } from './components/CardCourseDisplay';
import { Button } from '../../components/buttons/Button';
import { CoursesCalendar } from './calendar/CoursesCalendar';
import { CoursesViewSwitcher, COURSES_VIEWS } from './components/CoursesViewSwitcher';
import { resetCalendar } from '../../redux/reducers/calendar';

export const CoursesPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const savedFilterData = JSON.parse(localStorage.getItem('filtersCourses') || 'null');
    const { user } = useSelector((state) => state.user);
    const { _id: userId, roles, typeUser } = user;
    const { courses, loading: loadingCourses } = useSelector((state) => state.course);

    const normalizeSavedSort = (sort) => {
        if (!sort) {
            return {
                value: 'desc',
                label: 'Mas reciente',
            };
        }
        if (typeof sort === 'string') {
            return optionsSort.find((option) => option.value === sort) || {
                value: sort,
                label: sort,
            };
        }
        if (typeof sort === 'object' && sort.value) {
            return {
                ...sort,
                label: sort.label || optionsSort.find((option) => option.value === sort.value)?.label || sort.value,
            };
        }
        return {
            value: 'desc',
            label: 'Mas reciente',
        };
    };

    const [rows, setRows] = useState([]);
    const [totalCourse, setTotalCourse] = useState(0);
    const [isOpenDrawerFilters, setIsOpenDrawerFilters] = useState(false);
    const [courseDrawer, setCourseDrawer] = useState(null);
    const [usePagination, setUsePagination] = useState(savedFilterData?.usePagination || false);
    const [viewMode, setViewMode] = useState(() => {
        if (['estudiante', 'profesor'].includes(typeUser)) {
            return COURSES_VIEWS.CALENDAR;
        }
        const saved = localStorage.getItem('coursesViewMode');
        if (Object.values(COURSES_VIEWS).includes(saved)) return saved;
        return savedFilterData?.usePagination ? COURSES_VIEWS.TABLE : COURSES_VIEWS.CARDS;
    });
    const [pageSizeOptions, setpageSizeOptions] = useState({
        pageSize: 15,
        page: 0,
        totalRows: 0
    });
    
    const [formData, setFormData] = useState({
        userId: userId,
        roles: roles,
        fromDate: savedFilterData?.fromDate ? savedFilterData.fromDate : '',
        toDate: savedFilterData?.toDate ? typeUser === 'estudiante' ? new Date(savedFilterData.toDate).toISOString() : savedFilterData.toDate : '',
        identifier: savedFilterData?.identifier ?  typeUser === 'estudiante' ? '': savedFilterData.identifier : '',
        teacher: savedFilterData?.teacher ? typeUser === 'estudiante' ? '' : savedFilterData.teacher : '',
        level: savedFilterData?.level ? typeUser === 'estudiante' ? [] : savedFilterData.level : [],
        status: savedFilterData?.status ? typeUser === 'estudiante' ? [] : savedFilterData.status : [],
        language: savedFilterData?.language ? typeUser === 'estudiante' ? [] : savedFilterData.language : [],
        days: savedFilterData?.days ? typeUser === 'estudiante' ? [] : savedFilterData.days : [],
        sort: savedFilterData?.sort ? normalizeSavedSort(savedFilterData.sort) : {
            "value": "desc",
            "label": "Mas reciente"
        },
        usePagination: usePagination,
    });

    const allowedViewTable = ['director', 'control escolar', 'ventas', 'cobranza'];
    const noAllowedViewFilters = ['estudiante', 'profesor'];
    /** Profesores y estudiantes solo usan el calendario en /cursos. */
    const isCalendarOnly = noAllowedViewFilters.includes(typeUser);

    const handleSortChange = (value) => {
        const nextSort = optionsSort.find((option) => option.value === value) || {
            value,
            label: value,
        };
        setFormData((prev) => ({ ...prev, sort: nextSort }));
        const filters = JSON.parse(localStorage.getItem('filtersCourses') || '{}');
        localStorage.setItem('filtersCourses', JSON.stringify({ ...filters, sort: nextSort }));
    };

    useEffect(() => {
        dispatch(getAllCoursesByFilters({
            userId: userId,
            roles: roles,
            fromDate: formData?.fromDate ? formData.fromDate : '',
            toDate: formData?.toDate ? formData.toDate : '',
            identifier: formData?.identifier ? formData.identifier.name : '',
            teacher: formData?.teacher ? formData.teacher._id : '',
            level: formData?.level ? formData.level : [],
            status: formData?.status ? formData.status : [],
            language: formData?.language ? formData.language : [],
            days: formData?.days ? formData.days : [],
            sort: formData?.sort ? formData.sort : {
                "value": "desc",
                "label": "Mas reciente"
            },
            page: pageSizeOptions.page,
            pageSize: pageSizeOptions.pageSize,
            usePagination: usePagination,
        }))
            .then(((result) => {
                if (result.status === 200) {
                    setpageSizeOptions({
                        pageSize: result.pageSize,
                        page: result.page,
                        totalRows: result.totalRows
                    })
                }
            }
            ));
        ;
    }, [formData.sort, usePagination]);

    useEffect(() => {
        dispatch(cleanActionSelectedCourse())
        dispatch(cleanActionNoticeMessages())

        return () => {
            dispatch(resetCalendar());
        };
    }, [dispatch]);

    /**
     * El calendario no altera la paginación del listado; al volver a tarjetas o
     * tabla se sincroniza `usePagination`, que es lo que decide qué se renderiza.
     */
    const handleViewModeChange = (nextView) => {
        if (isCalendarOnly) return;

        setViewMode(nextView);
        localStorage.setItem('coursesViewMode', nextView);

        if (nextView === COURSES_VIEWS.CALENDAR) return;

        if ((nextView === COURSES_VIEWS.TABLE) !== usePagination) {
            handleViewCourse();
        }
    };

    useEffect(() => {
        if (isCalendarOnly && viewMode !== COURSES_VIEWS.CALENDAR) {
            setViewMode(COURSES_VIEWS.CALENDAR);
        }
    }, [isCalendarOnly, viewMode]);

    useEffect(() => {
        setRows(courses);
    }, [courses]);

    useEffect(() => {
        if (usePagination) {
            setTotalCourse(pageSizeOptions.totalRows);
        }
        if (!usePagination) {
            setTotalCourse(courses.length);
        }

    }, [pageSizeOptions, courses]);

    useEffect(() => {
        if (savedFilterData) {
            dispatch(getAllCoursesByFilters({
                userId: userId,
                roles: roles,
                fromDate: formData?.fromDate ? formData.fromDate : '',
                toDate: formData?.toDate ? formData.toDate : '',
                identifier: formData?.identifier ? formData.identifier.name : '',
                teacher: formData?.teacher ? formData.teacher._id : '',
                level: formData?.level ? formData.level : [],
                status: formData?.status ? formData.status : [],
                language: formData?.language ? formData.language : [],
                days: formData?.days ? formData.days : [],
                sort: formData?.sort ? formData.sort : {
                    "value": "desc",
                    "label": "Mas reciente"
                },
                page: pageSizeOptions.page,
                pageSize: pageSizeOptions.pageSize,
                usePagination: usePagination,
            }));
        } else {
            dispatch(getAllCoursesByFilters({
                userId: userId,
                roles: roles,
                fromDate: '',
                toDate: '',
                identifier: '',
                teacher: '',
                level: [],
                status: [],
                language: [],
                days: [],
                sort: {
                    "value": "desc",
                    "label": "Mas reciente"
                },
                page: pageSizeOptions.page,
                pageSize: pageSizeOptions.pageSize,
                usePagination: usePagination,
            }))
                .then(((result) => {
                    if (result.status === 200) {
                        setpageSizeOptions({
                            pageSize: result.pageSize,
                            page: result.page,
                            totalRows: result.totalRows
                        })
                    }
                }
                ));
        }
    }, []);

    const refetchCourses = useCallback(() => {
        dispatch(getAllCoursesByFilters({
            userId: userId,
            roles: roles,
            fromDate: formData?.fromDate ? formData.fromDate : '',
            toDate: formData?.toDate ? formData.toDate : '',
            identifier: formData?.identifier ? formData.identifier.name : '',
            teacher: formData?.teacher ? formData.teacher._id : '',
            level: formData?.level ? formData.level : [],
            status: formData?.status ? formData.status : [],
            language: formData?.language ? formData.language : [],
            days: formData?.days ? formData.days : [],
            sort: formData?.sort ? formData.sort : {
                value: 'desc',
                label: 'Mas reciente',
            },
            page: pageSizeOptions.page,
            pageSize: pageSizeOptions.pageSize,
            usePagination: usePagination,
        })).then((result) => {
            if (result?.status === 200) {
                setpageSizeOptions({
                    pageSize: result.pageSize,
                    page: result.page,
                    totalRows: result.totalRows,
                });
            }
        });
    }, [dispatch, userId, roles, formData, pageSizeOptions.page, pageSizeOptions.pageSize, usePagination]);

    useEffect(() => {
        const state = location.state;
        if (!state?.openCourseDrawer) return;
        if (state.openCourseDrawer === 'create') {
            setCourseDrawer({ mode: 'create' });
        } else if (state.openCourseDrawer === 'edit' && state.courseId) {
            setCourseDrawer({ mode: 'edit', courseId: state.courseId });
        }
        navigate(location.pathname, { replace: true, state: {} });
    }, [location.state, location.pathname, navigate]);

    const handleCreateNewCourse = () => {
        setCourseDrawer({ mode: 'create' });
    };

    const handleEditCourse = (editCourseId) => {
        setCourseDrawer({ mode: 'edit', courseId: editCourseId });
    };

    const handleCloseCourseDrawer = () => {
        setCourseDrawer(null);
    };

    const toggleDrawerFilters = () => {
        setIsOpenDrawerFilters((prevState) => !prevState);
    }

    const handleApllyFilters = () => {
        localStorage.setItem('filtersCourses', JSON.stringify({
            fromDate: formData.fromDate ? formData.fromDate : '',
            toDate: formData.toDate ? formData.toDate : '',
            identifier: formData.identifier ? formData.identifier : '',
            teacher: formData.teacher ? formData.teacher : '',
            level: formData.level ? formData.level : [],
            status: formData.status ? formData.status : [],
            language: formData.language ? formData.language : [],
            days: formData.days ? formData.days : [],
            sort: formData.sort ? formData.sort : {
                "value": "desc",
                "label": "Mas reciente"
            },
            usePagination: usePagination,
        }));
        dispatch(getAllCoursesByFilters({
            userId: userId,
            roles: roles,
            fromDate: formData?.fromDate ? formData.fromDate : '',
            toDate: formData?.toDate ? formData.toDate : '',
            identifier: formData?.identifier ? formData.identifier.name : '',
            teacher: formData?.teacher ? formData.teacher._id : '',
            level: formData?.level ? formData.level : [],
            status: formData?.status ? formData.status : [],
            language: formData?.language ? formData.language : [],
            days: formData?.days ? formData.days : [],
            sort: formData?.sort ? formData.sort : {
                "value": "desc",
                "label": "Mas reciente"
            },
            page: pageSizeOptions.page,
            pageSize: pageSizeOptions.pageSize,
            usePagination: usePagination,
        }))
            .then(((result) => {
                if (result.status === 200) {
                    setpageSizeOptions({
                        pageSize: result.pageSize,
                        page: result.page,
                        totalRows: result.totalRows
                    })
                }
            }
            ));
        toggleDrawerFilters();
    }

    const handleRemoveFilter = () => {
        setFormData({
            userId: userId,
            roles: roles,
            fromDate: '',
            toDate: '',
            identifier: '',
            teacher: '',
            level: [],
            status: [],
            language: [],
            days: [],
            sort: {
                "value": "desc",
                "label": "Mas reciente"
            },
            usePagination: usePagination,
        });
        localStorage.setItem('filtersCourses', JSON.stringify({
            fromDate: '',
            toDate: '',
            identifier: '',
            teacher: '',
            level: [],
            status: [],
            language: [],
            days: [],
            sort: {
                "value": "desc",
                "label": "Mas reciente"
            },
            usePagination: usePagination,
        }));
        dispatch(getAllCoursesByFilters({
            userId,
            roles,
            fromDate: '',
            toDate: '',
            identifier: '',
            teacher: '',
            level: [],
            status: [],
            language: [],
            days: [],
            sort: {
                "value": "desc",
                "label": "Mas reciente"
            },
            page: pageSizeOptions.page,
            pageSize: pageSizeOptions.pageSize,
            usePagination: usePagination,
        }))
            .then(((result) => {
                if (result.status === 200) {
                    setpageSizeOptions({
                        pageSize: result.pageSize,
                        page: result.page,
                        totalRows: result.totalRows
                    })
                }
            }
            ));
        toggleDrawerFilters();
    }

    const handlePageChange = (model) => {
        setpageSizeOptions(model);
        dispatch(getAllCoursesByFilters(
            {
                userId: userId,
                roles: roles,
                fromDate: formData?.fromDate ? formData.fromDate : '',
                toDate: formData?.toDate ? formData.toDate : '',
                identifier: formData?.identifier ? formData.identifier.name : '',
                teacher: formData?.teacher ? formData.teacher._id : '',
                level: formData?.level ? formData.level : [],
                status: formData?.status ? formData.status : [],
                language: formData?.language ? formData.language : [],
                days: formData?.days ? formData.days : [],
                sort: formData?.sort ? formData.sort : {
                    "value": "desc",
                    "label": "Mas reciente"
                },
                page: model.page,
                pageSize: model.pageSize,
                usePagination: true,

            }
        ))
            .then(((result) => {
                if (result.status === 200) {
                    setpageSizeOptions({
                        pageSize: result.pageSize,
                        page: result.page,
                        totalRows: result.totalRows
                    })
                }
            }
            ));
    }

    const handleViewCourse = () => {
        const filters = JSON.parse(localStorage.getItem('filtersCourses') || '{}');
        filters.usePagination = !usePagination;
        localStorage.setItem('filtersCourses', JSON.stringify(filters));
        setUsePagination(!usePagination);
    }


    const handleImportData = async (name) => {
        dispatch(verifyStateApp())
            .then((response) => {
                if (response.data === false) {
                    toast.error(response.message)

                } else {
                    confirDownloadFile(name);
                }
            });
    }

    const confirDownloadFile = async (name) => {
        let excelData = [];
        dispatch(getAllCoursesByFilters(
            {
                userId: userId,
                roles: roles,
                fromDate: formData?.fromDate ? formData.fromDate : '',
                toDate: formData?.toDate ? formData.toDate : '',
                identifier: formData?.identifier ? formData.identifier.name : '',
                teacher: formData?.teacher ? formData.teacher._id : '',
                level: formData?.level ? formData.level : [],
                status: formData?.status ? formData.status : [],
                language: formData?.language ? formData.language : [],
                days: formData?.days ? formData.days : [],
                sort: formData?.sort ? formData.sort : {
                    "value": "desc",
                    "label": "Mas reciente"
                },
                page: pageSizeOptions.page,
                pageSize: pageSizeOptions.pageSize,
                usePagination: usePagination,
                sendExcel: true,
            }
        ))
            .then(((result) => {
                if (result.status === 200) {
                    excelData = result.excelData
                    const response = exportToExcel(excelData, name);
                    response.data
                        ? toast.error(response.message)
                        : toast.success(response.message);
                }
            }
            ));

    }

    const isCalendarView = viewMode === COURSES_VIEWS.CALENDAR;

    /**
     * Filtros compartidos que el calendario puede honrar. Las fechas quedan
     * fuera: el calendario define su propio rango al navegar.
     */
    const calendarFilters = useMemo(() => ({
        identifier: formData.identifier?.name || '',
        teacher: formData.teacher?._id || '',
        language: formData.language || [],
        level: formData.level || [],
        status: formData.status || [],
        days: formData.days || [],
    }), [
        formData.identifier,
        formData.teacher,
        formData.language,
        formData.level,
        formData.status,
        formData.days,
    ]);

    return (
        <ContainerFull>
            <Heading
                title={isCalendarView
                    ? 'Calendario de cursos'
                    : `${roles === 'admin' ? `Total de cursos encontrados ${totalCourse}` : 'Listado de cursos'}`}
                subtitle={isCalendarView ? 'Clases y entregas de los cursos que puedes consultar' : ''}
                center={false}
            />
            {
                !isCalendarOnly && (
                <div className='w-full flex flex-col md:flex-row justify-center items-center md:justify-end mt-4 gap-4 shrink-0'>
                    <div className='w-full flex md:w-auto md:mr-auto'>
                        <CoursesViewSwitcher
                            value={viewMode}
                            onChange={handleViewModeChange}
                            allowTable={allowedViewTable.includes(typeUser)}
                        />
                    </div>
                    {
                        !isCalendarView && roles === 'admin' && (
                            <Tooltip title="Ordenar cursos" TransitionComponent={Zoom} placement='top'>
                                <div className='w-full flex md:w-auto'>
                                    <PopoverRadioFilter
                                        value={formData.sort?.value}
                                        onChange={handleSortChange}
                                        options={optionsSort}
                                        title='Ordenar cursos'
                                        subtitle='Define el orden en que se muestran los cursos.'
                                        icon={ArrowsUpDownIcon}
                                        placeholderLabel='Ordenar por'
                                    />
                                </div>
                            </Tooltip>
                        )
                    }
                    {
                        !noAllowedViewFilters.includes(typeUser) && (
                            <div className='w-full flex md:w-auto'>
                                <Tooltip title="Panel de filtros" TransitionComponent={Zoom} placement='top'>
                                    <Button
                                        onClick={toggleDrawerFilters}
                                        variant='barFilterSecondary'
                                        icon={<AdjustmentsHorizontalIcon className="block h-5 w-5" aria-hidden="true" />}
                                        className='w-full md:w-auto'
                                    >
                                        Filtros
                                    </Button>
                                </Tooltip>
                            </div>
                        )
                    }
                    {
                        typeUser === 'director' && (
                            <div className='w-full flex md:w-auto'>
                                <Tooltip title="Descargar archivo de Excel" TransitionComponent={Zoom} placement='top'>
                                    <Button
                                        onClick={() => handleImportData('Lista_de_cursos.xlsx')}
                                        variant='barFilterSecondary'
                                        icon={<DocumentArrowDownIcon className="block h-5 w-5" aria-hidden="true" />}
                                        className='w-full md:w-auto'
                                    >
                                        Exportar lista
                                    </Button>
                                </Tooltip>
                            </div>
                        )
                    }
                    {
                        typeUser === 'director' && (
                            <div className='w-full flex md:w-auto'>
                                <Tooltip title="Agregar un nuevo curso" TransitionComponent={Zoom} placement='top'>
                                    <Button
                                        onClick={handleCreateNewCourse}
                                        variant='barFilterPrimary'
                                        icon={<SquaresPlusIcon className="block h-5 w-5" aria-hidden="true" />}
                                        className='w-full md:w-auto'
                                    >
                                        Añadir curso
                                    </Button>
                                </Tooltip>
                            </div>
                        )
                    }
                </div>
                )
            }

            {isCalendarView ? (
                <div className="w-full mt-4">
                    <CoursesCalendar
                        filters={calendarFilters}
                        onCourseUpdated={refetchCourses}
                    />
                </div>
            ) : (
            <Wrapper>
                <div className="w-full">
                    {(roles === 'admin' || (roles === 'user' && typeUser === 'profesor')) ? (
                        <div className={`w-full grid grid-cols-1 md:${usePagination ? 'grid-cols-1' : rows.length > 0 ? 'grid-cols-2' : 'grid-cols-1'} lg:${usePagination ? 'grid-cols-1' : rows.length > 0 ? 'grid-cols-3' : 'grid-cols-1'} gap-4`}>
                            {
                                loadingCourses && usePagination && (
                                    <SkeletonTableCourse />
                                )
                            }
                            {
                                loadingCourses && !usePagination && (
                                    <>
                                        <SkeletonCardCourse />
                                        <SkeletonCardCourse />
                                        <SkeletonCardCourse />
                                    </>
                                )
                            }
                            {
                                !loadingCourses &&
                                !usePagination &&
                                rows.length > 0 &&
                                rows.map(course => (
                                    <CardCourse
                                        key={course.id}
                                        isCreating={false}
                                        idCourse={course?.id}
                                        onEditCourse={handleEditCourse}
                                        identifier={course?.identifier}
                                        color={course?.color}
                                        language={course?.language}
                                        path={course?.path}
                                        nivel={course?.level}
                                        studentLimit={course.limitMembers}
                                        status={course?.status}
                                        hours={course?.hours}
                                        days={course?.days}
                                        teacher={course?.teacher}
                                        fromDate={course?.fromDate}
                                        toDate={course?.toDate}
                                        headerImage={course?.headerImage?.urlName}
                                        updatedBy={course?.updatedBy}
                                        updatedAt={course?.updatedAt}
                                        totalStudents={course?.totalStudents}
                                        expiredPaymentDeadline={course?.expiredPaymentDeadline}
                                        validPaymentDeadline={course?.validPaymentDeadline}
                                    />
                                ))
                            }
                            {
                                !loadingCourses &&
                                usePagination &&
                                rows.length > 0 &&
                                <CourseTableView
                                    rows={rows}
                                    pageSizeOptions={pageSizeOptions}
                                    handlePageChange={handlePageChange}
                                    onEditCourse={handleEditCourse}
                                />
                            }
                            {
                                !loadingCourses &&
                                rows.length === 0 &&
                                (
                                    <div className="flex flex-col items-center justify-center w-full h-[500px] text-center ">
                                        <div className=" bg-gray-100 rounded-full p-6 mb-4 ">
                                            <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                            </svg>
                                        </div>
                                        <h4 className="text-lg font-semibold text-gray-700 mb-2">No hay cursos registrados</h4>
                                        <p className="text-sm text-gray-500">No se encontraron cursos con los filtros aplicados</p>
                                    </div>
                                )
                            }
                        </div>
                    ) : (
                        <div className={`w-full grid grid-cols-1 ${rows.length > 0 ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-1'} gap-4`}>
                            {loadingCourses
                                ? (
                                    <>
                                        <SkeletonCardCourse />
                                        <SkeletonCardCourse />
                                        <SkeletonCardCourse />
                                    </>
                                ) : rows.length > 0 ?
                                    (rows && rows.map(course => (
                                        <CardCourseDisplay
                                            id={course.id}
                                            headerImage={course?.headerImage?.urlName}
                                            language={course?.language}
                                            path={course?.path}
                                            color={course?.color}
                                            level={course?.level}
                                            status={course?.status}
                                            teacher={course?.teacher}
                                            fromDate={course?.fromDate}
                                            toDate={course?.toDate}
                                            days={course?.days}
                                            hours={course?.hours}
                                            identifier={course?.identifier}
                                            action={true}
                                        />
                                    ))) : (
                                        <div className="flex flex-col items-center justify-center w-full h-[500px] text-center ">
                                            <div className="bg-gray-100 rounded-full p-6 mb-4">
                                                <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                                </svg>
                                            </div>
                                            <h4 className="text-lg font-semibold text-gray-700 mb-2">No hay cursos registrados</h4>
                                            <p className="text-sm text-gray-500">Aún no se han inscrito cursos para este usuario</p>
                                        </div>
                                    )
                            }
                        </div>
                    )
                    }

                </div>
            </Wrapper>
            )}
            <DrawerFiltersCourse
                isOpenDrawerFilters={isOpenDrawerFilters}
                toggleDrawerFilters={toggleDrawerFilters}
                formData={formData}
                setFormData={setFormData}
                handleApllyFilters={handleApllyFilters}
                handleRemoveFilter={handleRemoveFilter}
            />
            <DrawerCourseForm
                isOpen={Boolean(courseDrawer)}
                courseId={courseDrawer?.mode === 'edit' ? courseDrawer.courseId : null}
                onClose={handleCloseCourseDrawer}
                onSuccess={refetchCourses}
            />
        </ContainerFull>
    )
}
